import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import {
  DashboardPayload,
  Dashboard,
  DashboardAPI,
  DuplicateDashboardPayload,
} from 'api/dashboard';
import { fiveMinutes } from './constants';

const DASHBOARDS_QUERY_KEY = 'dashboards';
const useDashboardsQuery = (
  options: UseQueryOptions<
    Dashboard[],
    unknown,
    Dashboard[],
    typeof DASHBOARDS_QUERY_KEY
  > = {},
): UseQueryResult<Dashboard[]> => {
  return useQuery({
    staleTime: fiveMinutes,
    ...options,
    queryKey: DASHBOARDS_QUERY_KEY,
    queryFn: () => DashboardAPI.getAllDashboards(),
  });
};

const DASHBOARD_VIEWS_QUERY_KEY = 'DASHBOARD_VIEWS_QUERY';

export const getDashboardViewsQueryKey = (): [string] => [
  DASHBOARD_VIEWS_QUERY_KEY,
];

const getDashboardQueryKey = (dashboardId: number): [string, string] => [
  'dashboards',
  `${dashboardId}`,
];
const useDashboardQuery = (dashboardId: number): UseQueryResult<Dashboard> => {
  return useQuery(getDashboardQueryKey(dashboardId), () =>
    DashboardAPI.getDashboard(dashboardId),
  );
};

const useCreateDashboardMutation = (): UseMutationResult<
  Dashboard,
  unknown,
  DashboardPayload
> => {
  const queryClient = useQueryClient();

  return useMutation((payload) => DashboardAPI.createDashboard(payload), {
    onSuccess: (newDashboard) => {
      queryClient.setQueryData<Dashboard[]>(
        DASHBOARDS_QUERY_KEY,
        (old = []) => [...old, newDashboard],
      );
    },
  });
};

const predicateById = (dashboardId) => (dashboard: Dashboard) =>
  dashboard.dashboardId === dashboardId;
const filterExceptId = (dashboardId) => (dashboard: Dashboard) =>
  dashboard.dashboardId !== dashboardId;

const updateDashboard =
  (newDashboard: Dashboard) =>
  (old: Dashboard[] = []) => {
    const idx = old.findIndex(predicateById(newDashboard.dashboardId));

    if (idx === -1) {
      return old;
    }

    return old.map((d) =>
      d.dashboardId !== newDashboard.dashboardId ? d : newDashboard,
    );
  };

const deleteDashboard =
  (idToBeDeleted: number) =>
  (old: Dashboard[] = []) => {
    return old.filter(filterExceptId(idToBeDeleted));
  };

interface DashboardPayloadWithId extends DashboardPayload {
  dashboardId: number;
}

const useUpdateDashboardMutation = (): UseMutationResult<
  Dashboard,
  unknown,
  DashboardPayloadWithId
> => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ dashboardId, ...rest }) =>
      DashboardAPI.updateDashboard(dashboardId, rest),
    {
      onSuccess: (newDashboard: Dashboard) => {
        queryClient.setQueryData<Dashboard[]>(
          DASHBOARDS_QUERY_KEY,
          updateDashboard(newDashboard),
        );
      },
    },
  );
};

const useDuplicateDashboardMutation = (): UseMutationResult<
  Dashboard,
  unknown,
  DuplicateDashboardPayload
> => {
  const queryClient = useQueryClient();

  return useMutation((payload) => DashboardAPI.duplicateDashboard(payload), {
    onSuccess: (newDashboard: Dashboard) => {
      queryClient.setQueryData<Dashboard[]>(
        DASHBOARDS_QUERY_KEY,
        (old = []) => [...old, newDashboard],
      );
    },
  });
};

const useDeleteDashboardMutation = (): UseMutationResult<
  Dashboard,
  unknown,
  number
> => {
  const queryClient = useQueryClient();

  return useMutation(
    (dashboardId: number) => DashboardAPI.deleteDashboard(dashboardId),
    {
      onSuccess: (_, dashboardId) => {
        queryClient.setQueryData<Dashboard[]>(
          DASHBOARDS_QUERY_KEY,
          deleteDashboard(dashboardId),
        );
      },
    },
  );
};

const useInitDashboardMutation = (): UseMutationResult<{
  dashboard: Dashboard[];
}> => {
  return useMutation(() => DashboardAPI.init());
};

export {
  DASHBOARDS_QUERY_KEY,
  useDashboardsQuery,
  getDashboardQueryKey,
  useDashboardQuery,
  useCreateDashboardMutation,
  useInitDashboardMutation,
  useDeleteDashboardMutation,
  useUpdateDashboardMutation,
  useDuplicateDashboardMutation,
};
